import React from "react";
import { useTranslation } from "@/context/LanguageContext";

interface RoomExamCreationHeroProps {
  totalCount: number;
  selectedCount: number;
  onToggleSelectAll: () => void;
  onContinue: () => void;
}

export const RoomExamCreationHero: React.FC<RoomExamCreationHeroProps> = ({
  totalCount,
  selectedCount,
  onToggleSelectAll,
  onContinue,
}) => {
  const { t, isRtl, formatNumber } = useTranslation();
  const allSelected = selectedCount === totalCount && totalCount > 0;

  return (
    <section className="mb-6 flex w-full items-center justify-center">
      <div className="flex min-h-[280px] w-full flex-col items-center gap-6 rounded-3xl border border-border bg-card px-6 pt-6 shadow-sm sm:min-h-[260px] lg:px-16">
        <div className="flex w-full items-center justify-center">
          <div className={`mb-6 flex w-1/3 max-w-md gap-2 ${isRtl ? "flex-row-reverse" : ""}`}>
            <div className="h-1.5 flex-1 rounded-full bg-muted">
              <div className="h-1.5 w-full rounded-full bg-emerald-500" />
            </div>
            <div className="h-1.5 flex-1 rounded-full bg-muted" />
            <div className="h-1.5 flex-1 rounded-full bg-muted" />
          </div>
        </div>

        <div className="flex max-w-2xl flex-col items-center gap-3 text-center">
          <h2 className="mt-1 text-xl font-normal leading-relaxed sm:text-2xl 2xl:text-3xl">
            {t("exam.chooseContents")}
          </h2>
          <p className="mb-2 text-base text-muted-foreground 2xl:text-lg">
            {t("exam.examWillBeGenerated")}
          </p>
        </div>

        <div className={`mb-2 flex flex-row items-center justify-center gap-5 ${isRtl ? "flex-row-reverse" : ""}`}>
          <button
            type="button"
            onClick={onToggleSelectAll}
            className={`flex cursor-pointer flex-row items-center gap-2 text-sm ${isRtl ? "flex-row-reverse" : ""}`}
          >
            <span
              data-state={allSelected ? "checked" : "unchecked"}
              className="flex h-5 w-5 items-center justify-center rounded-[6px] border border-primary/60 bg-background text-primary"
            >
              {allSelected && (
                <svg
                  className="h-3.5 w-3.5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M20 6 9 17l-5-5" />
                </svg>
              )}
            </span>
            <span>
              {t("common.selectAll")}{" "}
              <span className={`text-xs text-muted-foreground ${isRtl ? "pr-0.5" : "pl-0.5"}`}>
                ({formatNumber(selectedCount)}/{formatNumber(totalCount)})
              </span>
            </span>
          </button>

          <button
            onClick={onContinue}
            className={`inline-flex h-10 items-center justify-center gap-1 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 ${isRtl ? "flex-row-reverse" : ""}`}
          >
            <span>{t("common.continue")}</span>
            <svg
              className={`h-4 w-4 ${isRtl ? "scale-x-[-1]" : ""}`}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M5 12h14" />
              <path d="m12 5 7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
};
